/**
 * localStorage keys that hold generated audio for one character message.
 * Message identity is encodeMessageKey(full text), shared by every voice and language.
 */

const audioStoragePrefix = (characterId, messageKey) =>
    `voice_audio_${String(characterId)}_${messageKey}`;

const readyStoragePrefix = (characterId, messageKey) =>
    `voice_ready_${String(characterId)}_${messageKey}`;

const prefixMatches = (storageKey, prefix) =>
    storageKey === prefix || storageKey.startsWith(`${prefix}_`);

export const matchesMessageAudioStorageKey = (storageKey, characterId, messageKey) => {
    if (!storageKey || messageKey == null || messageKey === '') return false;
    const audioPrefix = audioStoragePrefix(characterId, messageKey);
    if (prefixMatches(storageKey, audioPrefix)) return true;
    if (prefixMatches(storageKey, `voice_chunks_${audioPrefix}`)) return true;
    if (prefixMatches(storageKey, `voice_meta_${audioPrefix}`)) return true;
    if (prefixMatches(storageKey, readyStoragePrefix(characterId, messageKey))) return true;
    return false;
};

export const renameMessageAudioStorageKey = (storageKey, characterId, previousMessageKey, nextMessageKey) => {
    if (!matchesMessageAudioStorageKey(storageKey, characterId, previousMessageKey)) return null;
    if (previousMessageKey === nextMessageKey) return storageKey;
    const fromAudio = audioStoragePrefix(characterId, previousMessageKey);
    const toAudio = audioStoragePrefix(characterId, nextMessageKey);
    const audioAt = storageKey.indexOf(fromAudio);
    if (audioAt !== -1) {
        return `${storageKey.slice(0, audioAt)}${toAudio}${storageKey.slice(audioAt + fromAudio.length)}`;
    }
    const fromReady = readyStoragePrefix(characterId, previousMessageKey);
    const toReady = readyStoragePrefix(characterId, nextMessageKey);
    if (storageKey.startsWith(fromReady)) {
        return `${toReady}${storageKey.slice(fromReady.length)}`;
    }
    return null;
};

const collectMatchingKeys = (storage, characterId, messageKey) => {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (matchesMessageAudioStorageKey(key, characterId, messageKey)) keys.push(key);
    }
    return keys;
};

/**
 * Delete every stored audio entry for this message, or move them onto nextMessageKey.
 * @param {Storage|{length:number,key:Function,getItem:Function,setItem:Function,removeItem:Function}} storage
 * @param {{ characterId: string|number, messageKey: string, nextMessageKey?: string|null }} change
 */
export const mutateMessageAudioStorage = (storage, { characterId, messageKey, nextMessageKey = null }) => {
    const keys = collectMatchingKeys(storage, characterId, messageKey);
    if (nextMessageKey == null) {
        keys.forEach((key) => storage.removeItem(key));
        return keys;
    }
    if (nextMessageKey === messageKey) return [];
    keys.forEach((key) => {
        const renamed = renameMessageAudioStorageKey(key, characterId, messageKey, nextMessageKey);
        if (!renamed || renamed === key) return;
        if (storage.getItem(renamed) == null) {
            const value = storage.getItem(key);
            if (value != null) storage.setItem(renamed, value);
        }
        storage.removeItem(key);
    });
    return keys;
};
